import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';
import { AlphaSignupModal } from './AlphaSignupModal';

interface LandingPageProps {
  onGetStarted: () => void;
}

export const LandingPage: React.FC<LandingPageProps> = ({ onGetStarted }) => {
  const [showSignupModal, setShowSignupModal] = useState(false);
  const navigate = useNavigate();
  
  const handleSignupClick = () => {
    setShowSignupModal(true);
  };
  
  const handleSignInClick = () => {
    navigate('/auth/login');
  };
  
  const handleSignupSuccess = () => {
    setShowSignupModal(false);
    // Optionally redirect to main app after successful signup
    // onGetStarted();
  };

  const features = [
    {
      icon: '🔒',
      title: 'HIPAA Compliant',
      description: 'Your health data is protected with enterprise-grade security and full HIPAA compliance using Azure OpenAI.'
    },
    {
      icon: '📊',
      title: 'Smart Data Extraction',
      description: 'Upload PDFs, CSVs, or Excel files. Our AI accurately extracts lab values with 95%+ accuracy.'
    },
    {
      icon: '📈',
      title: 'Track Trends Over Time',
      description: 'Visualize your health markers with interactive charts. Spot patterns and track progress.'
    },
    {
      icon: '🤖',
      title: 'AI-Powered Insights',
      description: 'Get personalized explanations of your lab results in plain English. No medical degree required.'
    },
    {
      icon: '🔍',
      title: 'Automatic Error Detection',
      description: 'Our AI detects and corrects common lab report errors like decimal misplacements and unit confusion.'
    },
    {
      icon: '⚡',
      title: 'Lightning Fast',
      description: 'Process hundreds of lab results in seconds. Access your data instantly from anywhere.'
    }
  ];

  // Alpha testing - no pricing during this phase

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="landing-nav">
        <div className="nav-container">
          <div className="nav-logo">
            <span className="logo-icon">📊</span>
            <span className="logo-text">UnderstandMyResults</span>
          </div>
          <div className="nav-links">
            <a href="#features">Features</a>
            <a href="#pricing">Pricing</a>
            <a href="#how-it-works">How it Works</a>
            <button className="nav-link-btn" onClick={handleSignInClick}>Sign In</button>
            <button className="nav-cta" onClick={handleSignupClick}>Request Access</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <div className="hipaa-badge">
            <span className="badge-icon">🔒</span>
            <span className="badge-text">HIPAA Compliant</span>
          </div>
          <h1>Your Lab Results, <span className="gradient-text">Simplified</span></h1>
          <p className="hero-subtitle">
            Upload your pathology reports and track your health markers over time. 
            Get AI-powered insights in plain English. Secure, private, and HIPAA compliant.
          </p>
          <div className="hero-cta">
            <button className="btn-primary" onClick={handleSignupClick}>
              Request Early Access
              <span className="cta-arrow">→</span>
            </button>
            <p className="cta-note">Limited spots available • Free during alpha</p>
          </div>
          <div className="hero-stats">
            <div className="stat">
              <span className="stat-number">95%+</span>
              <span className="stat-label">Accuracy</span>
            </div>
            <div className="stat">
              <span className="stat-number">HIPAA</span>
              <span className="stat-label">Compliant</span>
            </div>
            <div className="stat">
              <span className="stat-number">10s</span>
              <span className="stat-label">Processing</span>
            </div>
          </div>
        </div>
        <div className="hero-visual">
          <div className="demo-screenshot">
            <div className="screenshot-header">
              <span className="dot"></span>
              <span className="dot"></span>
              <span className="dot"></span>
            </div>
            <div className="screenshot-content">
              <div className="chart-preview">
                <svg viewBox="0 0 400 200" className="trend-chart">
                  <path d="M 50 150 Q 150 100 250 120 T 350 80" stroke="#3B82F6" strokeWidth="3" fill="none"/>
                  <circle cx="250" cy="120" r="5" fill="#3B82F6"/>
                  <text x="250" y="105" textAnchor="middle" className="chart-label">HDL: 65 mg/dL</text>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features">
        <div className="section-container">
          <h2>Everything You Need to Track Your Health</h2>
          <p className="section-subtitle">
            Built with privacy and security at its core. Your data never leaves our HIPAA-compliant infrastructure.
          </p>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="how-it-works">
        <div className="section-container">
          <h2>How It Works</h2>
          <div className="steps">
            <div className="step">
              <div className="step-number">1</div>
              <h3>Upload Your Reports</h3>
              <p>Drag and drop PDFs, CSVs, or Excel files. We support all major lab formats.</p>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <h3>AI Extracts Data</h3>
              <p>Our HIPAA-compliant AI accurately extracts lab values and validates for errors.</p>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <h3>Track & Understand</h3>
              <p>View trends, get insights, and understand your health markers in plain English.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Alpha Testing Section */}
      <section id="alpha" className="alpha-section">
        <div className="section-container">
          <h2>Join Our Alpha Testing Program</h2>
          <p className="section-subtitle">Help us build the future of health data management</p>
          <div className="alpha-benefits">
            <div className="alpha-card">
              <div className="alpha-icon">🎯</div>
              <h3>Early Access</h3>
              <p>Be among the first to use SimpleLabs and shape its development with your feedback.</p>
            </div>
            <div className="alpha-card">
              <div className="alpha-icon">🆓</div>
              <h3>Free During Alpha</h3>
              <p>Full access to all features at no cost during the alpha testing period.</p>
            </div>
            <div className="alpha-card">
              <div className="alpha-icon">💬</div>
              <h3>Direct Impact</h3>
              <p>Your feedback directly influences our product roadmap and feature prioritization.</p>
            </div>
            <div className="alpha-card">
              <div className="alpha-icon">🎁</div>
              <h3>Lifetime Discount</h3>
              <p>Alpha testers receive special pricing when we launch officially.</p>
            </div>
          </div>
          <div className="alpha-requirements">
            <h3>Alpha Testing Requirements</h3>
            <ul>
              <li>Currently available for US residents only</li>
              <li>EU/UK support coming soon with full GDPR compliance</li>
              <li>Willing to provide feedback on your experience</li>
              <li>Understanding that features may change during testing</li>
              <li>Comfortable with occasional bugs or downtime</li>
            </ul>
          </div>
          <div className="gdpr-notice">
            <h3>🇪🇺 A Note for EU/UK Users</h3>
            <p>
              We're working on full GDPR compliance for our European users. 
              While UnderstandMyResults is HIPAA compliant for US healthcare data, 
              we want to ensure we meet all EU privacy requirements before 
              accepting European testers. Join our waitlist to be notified 
              when we launch in your region.
            </p>
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="security">
        <div className="section-container">
          <div className="security-content">
            <h2>Your Privacy is Our Priority</h2>
            <div className="security-features">
              <div className="security-item">
                <span className="security-icon">🔒</span>
                <div>
                  <h4>HIPAA Compliant</h4>
                  <p>Full compliance with healthcare privacy regulations. Your data is encrypted at rest and in transit.</p>
                </div>
              </div>
              <div className="security-item">
                <span className="security-icon">🏥</span>
                <div>
                  <h4>Healthcare-Grade Security</h4>
                  <p>Built on Microsoft Azure's HIPAA-compliant infrastructure with BAA agreements in place.</p>
                </div>
              </div>
              <div className="security-item">
                <span className="security-icon">🚫</span>
                <div>
                  <h4>No Data Sharing</h4>
                  <p>Your health data is never shared or sold. You maintain complete control over your information.</p>
                </div>
              </div>
              <div className="security-item">
                <span className="security-icon">🗑️</span>
                <div>
                  <h4>Delete Anytime</h4>
                  <p>Export or permanently delete your data at any time. No questions asked.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="final-cta">
        <div className="section-container">
          <h2>Take Control of Your Health Data Today</h2>
          <p>Be among the first to experience a better way to track your health markers.</p>
          <button className="btn-primary large" onClick={handleSignupClick}>
            Request Early Access
            <span className="cta-arrow">→</span>
          </button>
          <p className="cta-note">Limited alpha spots • HIPAA compliant • Free during testing</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-container">
          <div className="footer-main">
            <div className="footer-brand">
              <div className="footer-logo">
                <span className="logo-icon">📊</span>
                <span className="logo-text">UnderstandMyResults</span>
              </div>
              <p>Making lab results simple to understand and track.</p>
              <div className="compliance-badges">
                <span className="compliance-badge">HIPAA Compliant</span>
                <span className="compliance-badge">SOC 2 Type II</span>
              </div>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4>Product</h4>
                <a href="#features">Features</a>
                <a href="#alpha">Alpha Program</a>
                <a href="#how-it-works">How it Works</a>
              </div>
              <div className="footer-column">
                <h4>Company</h4>
                <a href="#" onClick={(e) => e.preventDefault()}>About</a>
                <a href="#" onClick={(e) => e.preventDefault()}>Blog</a>
                <a href="#" onClick={(e) => e.preventDefault()}>Careers</a>
              </div>
              <div className="footer-column">
                <h4>Legal</h4>
                <a href="#" onClick={(e) => e.preventDefault()}>Privacy Policy</a>
                <a href="#" onClick={(e) => e.preventDefault()}>Terms of Service</a>
                <a href="#" onClick={(e) => e.preventDefault()}>HIPAA Notice</a>
              </div>
              <div className="footer-column">
                <h4>Support</h4>
                <a href="#" onClick={(e) => e.preventDefault()}>Help Center</a>
                <a href="mailto:support@understandmyresults.com">Contact Us</a>
                <a href="#" onClick={(e) => e.preventDefault()}>System Status</a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 UnderstandMyResults. All rights reserved.</p>
            <p className="footer-note">
              <strong>Alpha Testing Notice:</strong> UnderstandMyResults is currently in alpha testing. 
              Features and pricing may change.
            </p>
          </div>
        </div>
      </footer>

      {/* Alpha Signup Modal */}
      {showSignupModal && (
        <AlphaSignupModal
          isOpen={showSignupModal}
          onClose={() => setShowSignupModal(false)}
          onSuccess={handleSignupSuccess}
        />
      )}
    </div>
  );
};